import React, {
  useEffect,
  useRef,
  KeyboardEvent,
  ReactNode,
  ReactElement,
} from "react";
import "./tabs.scss";

export interface TabsProps {
  children: ReactNode;
  defaultActive?: number;
  activeIndex: number,
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
}

function flattenChildren(children: ReactNode): ReactElement[] {
  const result: ReactElement[] = [];
  React.Children.forEach(children, (child) => {
    if (!child) return;
    if (React.isValidElement(child)) {
      result.push(child);
      if (child.props?.children) {
        result.push(...flattenChildren(child.props.children));
      }
    }
  });
  return result;
}

export const Tabs: React.FC<TabsProps> = ({ children, defaultActive = 0, activeIndex, setActiveIndex }) => {
  const allChildren = flattenChildren(children);
  const tabs = allChildren.filter(
    (c) => (c.type as any).displayName === "Tab"
  );
  const panels = allChildren.filter(
    (c) => (c.type as any).displayName === "TabPanel"
  );

  const tabRefs = useRef<HTMLButtonElement[]>([]);
  const inkRef = useRef<HTMLDivElement>(null);

  const moveInk = () => {
    const tab = tabRefs.current[activeIndex];
    if (tab && inkRef.current) {
      const rect = tab.getBoundingClientRect();
      const parentRect = tab.parentElement?.getBoundingClientRect();
      if (parentRect) {
        inkRef.current.style.left = `${rect.left - parentRect.left}px`;
        inkRef.current.style.width = `${rect.width}px`;
      }
    }
  };

  useEffect(() => {
    moveInk();
    window.addEventListener("resize", moveInk);
    return () => window.removeEventListener("resize", moveInk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const total = tabs.length;
    let newIndex = activeIndex;

    switch (e.key) {
      case "ArrowRight":
        newIndex = (activeIndex + 1) % total;
        break;
      case "ArrowLeft":
        newIndex = (activeIndex - 1 + total) % total;
        break;
      case "Home":
        newIndex = 0;
        break;
      case "End":
        newIndex = total - 1;
        break;
      default:
        return;
    }

    setActiveIndex(newIndex);
    e.preventDefault();
  };

  if (!tabs.length || !panels.length) {
    console.warn("⚠️ Tabs: No <Tab> or <TabPanel> elements found.");
  }

  return (
    <div className="tabs">
      <div className="tablist-wrap">
        <div
          role="tablist"
          aria-label="Tabs"
          className="tablist"
          onKeyDown={handleKeyDown}
        >
          {tabs.map((child, index) =>
            React.cloneElement(child, {
              key: index,
              isActive: index === activeIndex,
              onClick: () => setActiveIndex(index),
              ref: (el: HTMLButtonElement) => (tabRefs.current[index] = el),
            })
          )}
          <div className="ink" ref={inkRef} aria-hidden="true" />
        </div>
      </div>

      <div className="panels">
        {panels.map((child, index) =>
          React.cloneElement(child, {
            key: index,
            isActive: index === activeIndex,
          })
        )}
      </div>
    </div>
  );
};
